import { Skeleton, type SkeletonProps, Box } from '@mui/material';
import { colors, radius, spacing } from '@/theme/tokens';

interface AppSkeletonProps extends SkeletonProps {
  rounded?: boolean;
}

export function AppSkeleton({ rounded = false, sx, ...props }: AppSkeletonProps) {
  return (
    <Skeleton
      animation="wave"
      sx={{
        backgroundColor: colors.surfaceRaised,
        borderRadius: rounded ? radius.full : radius.sm,
        '&::after': {
          background: `linear-gradient(90deg, transparent, ${colors.border}, transparent)`,
        },
        ...sx,
      }}
      {...props}
    />
  );
}

// Text skeleton with different sizes
interface AppTextSkeletonProps {
  lines?: number;
  lastLineWidth?: string;
}

export function AppTextSkeleton({ lines = 1, lastLineWidth = '75%' }: AppTextSkeletonProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[2] }}>
      {Array.from({ length: lines }).map((_, index) => (
        <AppSkeleton
          key={index}
          variant="text"
          width={index === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
          height={20}
        />
      ))}
    </Box>
  );
}

// Card skeleton
export function AppCardSkeleton() {
  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.lg,
        padding: spacing[6],
      }}
    >
      <AppSkeleton variant="text" width="40%" height={28} sx={{ marginBottom: spacing[4] }} />
      <AppTextSkeleton lines={3} />
    </Box>
  );
}

// Table row skeleton
interface AppTableRowSkeletonProps {
  columns: number;
  rows?: number;
}

export function AppTableRowSkeleton({ columns, rows = 5 }: AppTableRowSkeletonProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box key={rowIndex} sx={{ display: 'flex', gap: spacing[4] }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <AppSkeleton key={colIndex} variant="rectangular" height={40} sx={{ flex: 1 }} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

// Avatar skeleton
interface AppAvatarSkeletonProps {
  size?: number;
}

export function AppAvatarSkeleton({ size = 40 }: AppAvatarSkeletonProps) {
  return <AppSkeleton variant="circular" width={size} height={size} />;
}
